import { Menu, Button, Text, Stack, Flex, Box, Timeline, Group, Modal, Loader, Avatar, Tooltip, createStyles } from '@mantine/core';
import {  IconTrash, IconDots, IconCheck, IconCirclePlus, IconPencil, IconClock, IconDownload, IconUpload } from '@tabler/icons';
import { useContext, useState } from 'react';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import IdeaContext from '../IdeaContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { closeAllModals, openConfirmModal, openModal } from '@mantine/modals';
import { deleteIdea, saveIdea, getIdeaActivityLogs } from '../../../Api';
import { ArchiveIcon, IconHistory, IdeasIcon, SmallIdeasIcon } from '../../../icons/x-symbol-svgrepo-com';
import { showNotification } from '@mantine/notifications';
import { downloadIdeaToCSV } from '../../../utils/downloadIdeaToCSV';
import { DeclineGeneratedIdeaModal } from './DeclineGeneratedIdeaModal';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { ideaKeys } from '../../../hooks/ideaPage/ideaKeys';
import { normalizeIdeaIdForQuery } from '../../../utils/normalizeIdeaId';

export function IdeaMenu() {

  const { idea,
    ideaState,
    isDeliveryLinkPresent,
    loadIdea,
    setIdeaHistoryView,
    setCardView,
    allRequests,
    matches,
    requestsMatches,
    setActivityLogs,
    ideaId,
  } = useContext(IdeaContext);
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(true);
  const [showHello, setShowHello] = useState(true);
  const { canEditIdeas } = useUserPermissions();
  // Hide "hello" after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowHello(false);
    }, 7000);
    return () => clearTimeout(timer);
  }, []);

  const openDeleteRequest = () =>{

    const validated = allRequests.filter((request: any) => request.status === 'validated')

    if(isDeliveryLinkPresent || validated.length > 0){
      openModal({
        modalId: "decline",
        title: <Text fz={20} fw={600} ml={20} mt={40}>Delete idea</Text>,
        centered: true,
        size: 496,
        styles: {
          close: {
            alignSelf: "flex-start",  
            marginTop: 10,
            marginRight: 10,
          },
          header:{
            padding: "10px 20px"
          },
          body: {
            height: 186, 
          },
        },
        children: (
          <Stack p={20}>
            <Box fz={14}>
              <Text>There are requests assigned to this idea.</Text>
              <Text> Please re-assign them before deleting the idea</Text>
            </Box>
            <Flex justify="end" mt="44px">
              <Button
                onClick={() => {
                  closeAllModals();
                }}
                styles={(theme) => ({
                  root: {
                    backgroundColor: "#5B60E3",
                    border: 0,
                    height: 32,
                    "&:hover": {
                      backgroundColor: theme.fn.darken("#5B60E3", 0.05),
                    },
                  },
                })}
                px={8}
                leftIcon={<IconCheck size={14} />}
              >
                OK
              </Button>
            </Flex>
          </Stack>
        ),
      });
    }
    else {
      if (idea?.status === "generated" || !canEditIdeas) {
        openModal({
          modalId: "decline-generated",
          title: 'Delete idea',
          centered: true,
          size: 600,
          children: (
            <DeclineGeneratedIdeaModal idea ={idea}  type={'decline'}/>
          )
        })
      } else {
        openConfirmModal({
          title: <Text fz={20} fw={600} ml={20} mt={40}>Delete idea</Text>,
          size: 496,
          centered: true,
          children: (
            <Text size="sm" mt={15} mb={30}>Are you sure you want to delete this idea? This operation will unlink any sub-Ideas and they will become top level Ideas</Text>
          ),
          styles: {
            close: {
              alignSelf: "flex-start",
              marginTop: 10,
              marginRight: 10,
            },
            header:{
              padding: "10px 20px"
            },
            body: {
              height: 120,
              padding: 40,
            },

          },
          labels: { confirm: "Delete idea", cancel: "No, keep it" },
          confirmProps: {
            color: "red",
            px: 8,
            styles: {
              label: {
                fontSize: 12,
                fontWeight: 500
              },
            },
            leftIcon: <IconTrash size={14} />,
          },
          cancelProps: {
            leftIcon: <IconCheck size={16} />,
            px: 8,
            styles: {
              label: {
                fontSize: 12,
                fontWeight: 500
              },
            },
          },
          onCancel: () => console.log("Cancel"),
          onConfirm: () => {
            deleteIdea(idea?._id, auth0)
              .then((response) => {
                navigate("/ideas");
              })
              .catch((e: any) => console.log(e));
          },
        });
      }
    }

  }

  const handleArchiveIdea = () => {
    const isArchived = ideaState === "archived";

    if (idea.status === "generated" || !canEditIdeas) {
      openModal({
        modalId: "archive-generated",
        title: 'Archive idea',
        centered: true,
        size: 600,
        children: (
          <DeclineGeneratedIdeaModal idea={idea} type={'archive'} />
        )
      })
    } else {
      const request = {
        id: idea?._id,
        state: isArchived ? "active" : "archived",
      };
      saveIdea(request, auth0)
        .then((res) => {
          loadIdea();
          showNotification({
            title: isArchived ? "Idea unarchived" : "Idea archived",
            color: "teal",
            message: <Text
              onClick={() => {
                request.state = isArchived ? "archived" : "active";
                saveIdea(request, auth0)
                  .then(() => {
                    loadIdea();
                    showNotification({
                      id: 'idea-archive',
                      title: isArchived ? "Idea archived" : "Idea unarchived",
                      message: `Idea has been restored to ${ideaState}`,
                      color: "teal",
                      autoClose: 3000,
                      icon: <IconCheck size="1.1rem" />,
                    });
                  })
                  .catch((e) => console.error("Undo failed", e));
              }}
              style={{
                color: "#228be6",
                cursor: "pointer",
                fontWeight: 500,
                display: "inline-block",
                marginTop: 8,
              }}
            >
              Undo
            </Text>,
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200
          });
        })
        .catch((e) => console.log(e));
    }
  }

  const ideaHistory = async () => {
    setIdeaHistoryView(true);
    setCardView(false);
    const id =
      (ideaId != null && String(ideaId).trim() !== '' ? String(ideaId).trim() : '') ||
      normalizeIdeaIdForQuery(idea?._id);
    if (!id) return;
    try {
      const response = await getIdeaActivityLogs(id, auth0);
      const logs = response?.activityLogs ?? [];
      queryClient.setQueryData(ideaKeys.activityLogs(id), logs);
      setActivityLogs(logs);
    } catch (e) {
      console.error(e);
    }
  };

  const hasOrigin: boolean = idea?.origin === 'jira' || idea?.origin === 'clickup' || idea?.origin === 'productboard'

  // Add this constant for allowed fields
  const ALLOWED_FIELDS = ['ownerName', 'componentId', 'status', 'state'];

  // Add this helper function to get readable field names
  const getReadableFieldName = (field: string) => {
    const fieldMap: { [key: string]: string } = {
      ownerName: 'Owner',
      componentId: 'Product Area',
      status: 'Status',
      state: 'State'
    };
    return fieldMap[field] || field;
  };

  return (
    <>
      <Menu withinPortal width={200}>
        <Menu.Target>
          <Button styles={(theme) => ({
            root: {
              height: "24px",
              width: "24px",
              backgroundColor: opened ?'rgba(92, 92, 235, 0.4)' : 'rgba(92, 92, 235, 0.1)',
              border: 0,
              fontWeight: 400,
              padding: 0,
              borderRadius:"4px",
              "&:hover": {
                backgroundColor: opened ?'rgba(92, 92, 235, 0.4)' : 'rgba(92, 92, 235, 0.1)',
              },
            },

            leftIcon: {
              marginRight: 15,
            },
          })}>
            <IconDots size={18} color='black'/>
          </Button>
          
          {/* </Stack> */}
        </Menu.Target>
        <Menu.Dropdown>
         
          <Menu.Item
            icon={<SmallIdeasIcon />}
            onClick={() => {
              localStorage.setItem('newPage', 'true');
              window.location.reload();
            }}
            color={"black" }
          >
               New idea page
          </Menu.Item>
          {ideaState === "active" || ideaState === "archived"}
          <Menu.Item
            onClick={ideaHistory}
            color={hasOrigin ? "black" : "black"}
            icon={<IconHistory/>}
          >
                Idea history
          </Menu.Item>
          <Menu.Item
            onClick={handleArchiveIdea}
            color={hasOrigin ? "black" : "black"}
            icon={<ArchiveIcon />}
          >
            {ideaState === "archived" ? "Unarchive idea" : "Archive idea"}
          </Menu.Item> 
          <Menu.Item
            onClick={() => downloadIdeaToCSV({ idea, allRequests, matches, requestsMatches })}
            color={hasOrigin ? "black" : "black"}
            icon={<IconDownload size={14} />}
          >
                Download idea
          </Menu.Item>
          <Tooltip
            label={hasOrigin ? "Cannot delete ideas from external sources [Jira, Clickup, Productboard]" : ""}
            disabled={!hasOrigin}
            position="left"
            width={200}
            withArrow
            multiline
          >
            <Menu.Item
              onClick={hasOrigin ? undefined : openDeleteRequest}
              color={hasOrigin ? "#808080" : "red"}
              icon={<IconTrash size={14} />}
              sx={{
                cursor: hasOrigin ? 'not-allowed' : 'pointer',
                opacity: hasOrigin ? 0.6 : 1,
              }}
            >
                  Delete idea
            </Menu.Item>
          </Tooltip> 
          
        </Menu.Dropdown>
      </Menu>
    </>
  );
}